import { cn } from '../utils/cn'
import { DropIndicator } from '../ui/sortable'
import * as React from 'react'
import { ReactNode } from 'react'
import {
	MultiSelectItemDragOverlayUI,
	MultiSelectItemRemoveButtonUI,
	MultiSelectItemUI,
	MultiSelectSortableItemContentUI,
	SelectCreateNewTrigger,
	SelectDefaultPlaceholderUI,
	SelectInputActionsUI,
	SelectInputUI,
	SelectInputWrapperUI,
	SelectListItemUI,
	SelectPopoverContent,
} from './ui'
import { Popover, PopoverTrigger } from '../ui/popover'
import { ChevronDownIcon } from 'lucide-react'
import { SelectListInner } from './list'
import {
	RepeaterSortable,
	RepeaterSortableDragOverlay,
	RepeaterSortableDropIndicator,
	RepeaterSortableEachItem,
	RepeaterSortableItemActivator,
	RepeaterSortableItemNode,
} from '@contember/react-repeater-dnd-kit'
import {
	Component,
	HasOne,
	SugaredQualifiedEntityList,
	SugaredRelativeEntityList,
	SugaredRelativeSingleEntity,
	SugaredRelativeSingleField,
} from '@contember/interface'
import { SelectDataView, SelectItemTrigger, SelectOption, SelectPlaceholder, SortableMultiSelect } from '@contember/react-select'
import { CreateEntityDialog } from './create-new'
import {
	createFilterHandler,
	createUnionTextFilter,
	DataViewFilterHandlerRegistry,
	DataViewQueryFilterName,
	DataViewSortingDirections,
	DataViewUnionFilterFields,
} from '@contember/react-dataview'
import { SelectDefaultFilter } from './filter'
import { useFormFieldId } from '@contember/react-form'

const MultiSortableSelectDropIndicator = ({ position }: { position: 'before' | 'after' }) => (
	<div className={cn('relative', position === 'before' ? '-translate-x-0.5' : 'translate-x-1.5')}>
		<RepeaterSortableDropIndicator position={position}>
			<DropIndicator placement={position === 'before' ? 'left' : 'right'} />
		</RepeaterSortableDropIndicator>
	</div>
)

export type SortableMultiSelectInputProps = {
	field: SugaredRelativeEntityList['field']
	sortableBy: SugaredRelativeSingleField['field']
	connectAt: SugaredRelativeSingleEntity['field']
	children: ReactNode
	options?: SugaredQualifiedEntityList['entities']
	placeholder?: ReactNode
	createNewForm?: ReactNode
	queryField?: DataViewUnionFilterFields
	initialSorting?: DataViewSortingDirections
}

export const SortableMultiSelectInput = Component<SortableMultiSelectInputProps>(
	({ field, queryField, options, children, sortableBy, connectAt, placeholder, createNewForm, initialSorting }) => {
		const id = useFormFieldId()

		// Handling UUIDs in queryField - same logic as SelectInput
		const filterTypes = React.useMemo((): DataViewFilterHandlerRegistry | undefined => {
			if (!queryField) {
				return undefined
			}
			const fields = Array.isArray(queryField) ? queryField : [queryField]
			const hasId = fields.includes('id')
			if (!hasId) {
				return undefined
			}
			const textFields = fields.filter(f => f !== 'id')
			const textHandler = textFields.length > 0 ? createUnionTextFilter(textFields) : undefined
			const idEqHandler = createFilterHandler<{ query?: string }>({
				createFilter: ({ query }) => {
					if (!query) return undefined
					const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(query)
					if (!isUuid) return undefined
					return { id: { eq: query } }
				},
				isEmpty: f => !f.query,
				identifier: { id: Symbol('idEq'), params: {} },
			})

			return {
				[DataViewQueryFilterName]: (artifact, options) => {
					const parts = []
					if (textHandler) {
						const text = textHandler(artifact, options)
						if (text) parts.push(text)
					}
					const idPart = idEqHandler(artifact, options)
					if (idPart) parts.push(idPart)
					if (parts.length === 0) return undefined
					return { or: parts as any }
				},
			}
		}, [queryField])

		return (
			<SortableMultiSelect field={field} sortableBy={sortableBy} connectAt={connectAt} options={options}>
				<div className="flex gap-1 items-center">
					<Popover>
						<SelectInputWrapperUI>
							<PopoverTrigger asChild>
								<SelectInputUI id={id ? `${id}-input` : undefined}>
									<SelectPlaceholder>{placeholder ?? <SelectDefaultPlaceholderUI />}</SelectPlaceholder>

									<RepeaterSortable>
										<RepeaterSortableEachItem>
											<div className={'flex'}>
												<MultiSortableSelectDropIndicator position={'before'} />
												<RepeaterSortableItemNode>
													<MultiSelectItemUI>
														<HasOne field={connectAt}>
															<RepeaterSortableItemActivator>
																<MultiSelectSortableItemContentUI>{children}</MultiSelectSortableItemContentUI>
															</RepeaterSortableItemActivator>

															<SelectItemTrigger>
																<MultiSelectItemRemoveButtonUI onClick={e => e.stopPropagation()} />
															</SelectItemTrigger>
														</HasOne>
													</MultiSelectItemUI>
												</RepeaterSortableItemNode>
												<MultiSortableSelectDropIndicator position={'after'} />
											</div>
										</RepeaterSortableEachItem>

										<RepeaterSortableDragOverlay>
											<MultiSelectItemDragOverlayUI>
												<HasOne field={connectAt}>{children}</HasOne>
											</MultiSelectItemDragOverlayUI>
										</RepeaterSortableDragOverlay>
									</RepeaterSortable>

									<SelectInputActionsUI>
										<ChevronDownIcon className={'w-4 h-4'} />
									</SelectInputActionsUI>
								</SelectInputUI>
							</PopoverTrigger>
						</SelectInputWrapperUI>

						<SelectPopoverContent>
							<SelectDataView initialSorting={initialSorting} queryField={queryField} filterTypes={filterTypes}>
								<SelectListInner filterToolbar={<SelectDefaultFilter />}>
									<SelectOption>
										<SelectItemTrigger>
											<SelectListItemUI>{children}</SelectListItemUI>
										</SelectItemTrigger>
									</SelectOption>
								</SelectListInner>
							</SelectDataView>
						</SelectPopoverContent>
					</Popover>
					{createNewForm && <CreateEntityDialog trigger={<SelectCreateNewTrigger />}>{createNewForm}</CreateEntityDialog>}
				</div>
			</SortableMultiSelect>
		)
	},
	({ children, field, sortableBy, connectAt, options }) => {
		return (
			<SortableMultiSelect field={field} sortableBy={sortableBy} connectAt={connectAt} options={options}>
				{children}
			</SortableMultiSelect>
		)
	},
)
